<script lang="ts">
	// Formatters
	import { format } from 'svelte-i18n';
	// Svelte hooks
	import { createEventDispatcher } from 'svelte';
	// Types
	import type { TextPropsTypes } from '@Shelly/shelly-design-system';
	import type { EventTableHeading } from './event_log_v2_ui_helpers';
	// Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	// Components
	import IconForInputReader from './IconForInputReader.svelte';
	import CameraEventExpanded from './CameraEventExpanded.svelte';
	import EventDetailsModalV2 from './EventDetailsModalV2.svelte';
	// Design system components
	import {
		ExpandableTable,
		ExpandableTableCol,
		ExpandableTableRow,
		Icon,
		IconButton,
		Text,
		LabelList,
		LabelListV2,
	} from '@Shelly/shelly-design-system';
	// Helpers
	import { timestampToDate } from 'src/helpers/date_helper';
	import { eventLogHasRecordingExpand } from './event_log_helper';
	import {
		getEventTableHeadingsV2,
		openEventSidePanel,
	} from './event_log_v2_ui_helpers';

	type EventTableColumn = {
		id: string;
		icon?: string | { value: string };
		iconStyle?: string;
		text?: string;
		labelList?: any[];
		isDeviceName?: boolean;
	};

	const dispatch = createEventDispatcher();

	export let events = [], // get event log data in UI format
		devicePageLog = false;

	export let type: 'global' | 'group' | 'device' = 'global';
	export let openedEventTimestamp = null;

	const EVENT_TABLE_HEADINGS = getEventTableHeadingsV2();

	/** Amount of property chips shown inside the row before the user expands it. */
	const PROPERTIES_PREVIEW_LENGTH = 2;

	const COL_WIDTHS_BY_TYPE = {
		global: ['36px', '1fr', '1fr', '1fr', '2fr', '36px'],
		group: ['1fr', '1fr', '1fr', '36px'],
		device: ['1fr', '36px'],
	};
	const COL_WIDTHS_MOBILE_BY_TYPE = {
		global: ['22px', '1fr', '1fr', '1fr', '22px'],
		group: ['1fr', '1fr', '1fr', '36px'],
		device: ['1fr', '36px'],
	};

	const HEADINGS_BY_TYPE = {
		global: [
			EVENT_TABLE_HEADINGS.deviceIcon,
			EVENT_TABLE_HEADINGS.timestamp,
			EVENT_TABLE_HEADINGS.room,
			EVENT_TABLE_HEADINGS.name,
			EVENT_TABLE_HEADINGS.properties,
			EVENT_TABLE_HEADINGS.info,
		],
		group: [
			EVENT_TABLE_HEADINGS.timestamp,
			EVENT_TABLE_HEADINGS.room,
			EVENT_TABLE_HEADINGS.name,
			EVENT_TABLE_HEADINGS.info,
		],
		device: [EVENT_TABLE_HEADINGS.timestamp, EVENT_TABLE_HEADINGS.info],
	};

	/** Mobile drops only the properties column, they are shown in the expanded row. */
	const HEADINGS_MOBILE_GLOBAL = [
		EVENT_TABLE_HEADINGS.deviceIcon,
		EVENT_TABLE_HEADINGS.timestamp,
		EVENT_TABLE_HEADINGS.room,
		EVENT_TABLE_HEADINGS.name,
		EVENT_TABLE_HEADINGS.info,
	];

	let isMobile: boolean;

	/** Event timestamps opened by the user or an external request. */
	let expandedEventTimestamps = new Set<number>();

	/** Event shown inside the mobile details modal. */
	let detailsEventTimestamp: number = null;

	$: isGlobalMobile = type === 'global' && isMobile;

	$: headingElements = isGlobalMobile ? HEADINGS_MOBILE_GLOBAL : HEADINGS_BY_TYPE[type];

	$: tableColWidths = isMobile ? COL_WIDTHS_MOBILE_BY_TYPE[type] : COL_WIDTHS_BY_TYPE[type];

	$: eventRows = events.map((event) => {
		const labelListData = Object.values(event.data).filter((k: any) => {
			if (k.hidden) return false;
			return ![
				'S',
				'SS',
				'SSS',
				'SL',
				'LS',
				'L',
				'LL',
				'status_update',
				'unknown',
				'undefined',
				'Locked',
				'Unlocked',
				'open',
			].includes(k.value);
		});

		if (/_100$/.test(event.id)) {
			labelListData.push({
				name: 'addon',
				value: $format('settings_title_addon_sensors', {
					default: 'Add-on peripherals',
				}),
				icon: null,
			});
		}

		return {
			event,
			notExpandable: labelListData.length === 0 && !eventLogHasRecordingExpand(event),
			labelListData,
		};
	});

	$: expandedRows = eventRows.map((row) => ({
		...row,
		expanded: expandedEventTimestamps.has(row.event.timestamp),
	}));

	$: expandableRows = expandedRows.filter((row) => !row.notExpandable);

	$: isAllExpanded = expandableRows.length > 0 && expandableRows.every((row) => row.expanded);

	$: isAllRowsNotExpandable = type === 'device' && expandableRows.length === 0;

	$: styleCondition = isMobile && !devicePageLog;

	$: detailsRow = eventRows.find((row) => row.event.timestamp === detailsEventTimestamp);

	$: if (!isMobile) detailsEventTimestamp = null;

	$: if (openedEventTimestamp) {
		openRequestedEvent(openedEventTimestamp);

		openedEventTimestamp = null;
	}

	/**
	 * Builds the columns of a single row, matching the heading columns of the current layout.
	 * Event object format => {timestamp: Date.now(), room: "", name: "" ,
	 * eventID: 0, data : [], units: undefined, type : "" }
	 */
	const prepareRowColumns = (
		event,
		labelListData: any[],
		isMobileLayout: boolean,
	): EventTableColumn[] => {
		const columns: EventTableColumn[] = [];

		if (type === 'global') {
			columns.push({
				id: EVENT_TABLE_HEADINGS.deviceIcon.id,
				icon: event.catIcon,
				iconStyle: '',
			});
		}

		columns.push({
			id: EVENT_TABLE_HEADINGS.timestamp.id,
			text: timestampToDate(event.timestamp),
		});

		if (type !== 'device') {
			columns.push(
				{ id: EVENT_TABLE_HEADINGS.room.id, text: event.room },
				{
					id: EVENT_TABLE_HEADINGS.name.id,
					text: event.name,
					isDeviceName: type === 'global',
				},
			);
		}

		if (type === 'global' && !isMobileLayout) {
			columns.push({
				id: EVENT_TABLE_HEADINGS.properties.id,
				labelList: labelListData.slice(0, PROPERTIES_PREVIEW_LENGTH),
			});
		}

		columns.push({
			id: EVENT_TABLE_HEADINGS.info.id,
			icon: event.icon?.icon,
			iconStyle: event.icon?.style,
		});

		return columns;
	};

	/** Expands an event requested from outside the table. */
	const openRequestedEvent = (timestamp: number) => {
		const eventExists = events.some((event) => event.timestamp === timestamp);

		if (eventExists) expandedEventTimestamps = new Set([timestamp]);
	};

	const handleHeadingClick = (heading: EventTableHeading) => {
		if (!heading?.sort) return;

		heading.desc = !heading.desc;
		dispatch('sortBy', { title: heading.id, desc: heading.desc });
	};

	const onExpand = (eventTimestamp: number) => {
		const otherExpandedTimestamps = Array.from(expandedEventTimestamps).filter(
			(expandedTimestamp) => expandedTimestamp !== eventTimestamp,
		);

		expandedEventTimestamps = expandedEventTimestamps.has(eventTimestamp)
			? new Set(otherExpandedTimestamps)
			: new Set(otherExpandedTimestamps.concat(eventTimestamp));
	};

	const onExpandAll = () => {
		expandedEventTimestamps = isAllExpanded
			? new Set()
			: new Set(expandableRows.map((row) => row.event.timestamp));
	};

	/** Opens the full details modal for the given row (mobile only). */
	const openDetails = (row) => {
		detailsEventTimestamp = row.event.timestamp;
	};

	const closeDetails = () => {
		detailsEventTimestamp = null;
	};

	const getTextType = (
		styleCondition: boolean,
		isTableHeading = false,
	): TextPropsTypes['styleType'] => {
		if (isTableHeading) {
			return styleCondition ? 'body-small' : 'h2';
		}
		return styleCondition ? 'tiny' : 'body-small';
	};

	const getIconSize = (styleCondition: boolean) => {
		return styleCondition ? 'xs' : 's';
	};

	const handleColumnClick = (e: MouseEvent, column: EventTableColumn, event) => {
		if (!column.isDeviceName) return;

		e.stopPropagation();
		openEventSidePanel(event);
	};

	if (import.meta.env.DEV) {
		console.log('%cEvents', 'color:lightblue; font-size:14px;', events);
	}
</script>

<ExpandableTable
	backgroundColor={$showNewLayout ? 'color-0' : undefined}
	rowsColor={$showNewLayout && type !== 'global' ? 'color-0' : 'color-base'}
	bind:isMobile
	colWidths={tableColWidths}
	class="mb-m"
	isSimple={type === 'group' || isAllRowsNotExpandable}
>
	<!-- Table Heading -->
	<ExpandableTableRow
		on:expand={onExpandAll}
		heading={true}
		expanded={isAllExpanded}
		notExpandable={isAllRowsNotExpandable}
	>
		{#each headingElements as col (col.id)}
			<ExpandableTableCol>
				{#if col.icon}
					<Icon class="width-full" icon={col.icon} size={getIconSize(styleCondition)} />
				{/if}
				{#if col.title && !col.icon}
					<Text
						styleType={getTextType(styleCondition, true)}
						breakWord
						class="text-capitalize text-bold"
						on:click={() => handleHeadingClick(col)}
					>
						{col.title}
					</Text>
				{/if}
			</ExpandableTableCol>
		{/each}
	</ExpandableTableRow>
	<!-- Table Heading -->

	<!-- Table Body -->
	{#each expandedRows as row (row.event)}
		<ExpandableTableRow
			on:expand={() => onExpand(row.event.timestamp)}
			heading={false}
			expanded={row.expanded}
			notExpandable={row.notExpandable}
			rowClickExpand
		>
			{#each prepareRowColumns(row.event, row.labelListData, isMobile) as col (col.id)}
				<ExpandableTableCol>
					{#if col.icon}
						{#if typeof col.icon !== 'string'}
							<!-- added object type for col.icon because the need to use custom icons -->
							<IconForInputReader
								class="width-full"
								shrinkOnMobile
								event={col.icon.value}
							/>
						{:else}
							<Icon
								class="width-full"
								icon={col.icon}
								size={getIconSize(styleCondition)}
								style={col.iconStyle}
							/>
						{/if}
					{:else if col.labelList}
						<LabelListV2
							a11yProps={{
								isInteractive: false,
							}}
							labelList={col.labelList}
							size="s"
							backgroundColor="--color-1"
						/>
					{:else if col.text}
						<Text
							class={col.isDeviceName ? 'link-styles text-small' : ''}
							styleType={getTextType(styleCondition)}
							breakWord
							on:click={(e) => handleColumnClick(e, col, row.event)}
						>
							{col.text}
						</Text>
					{/if}
				</ExpandableTableCol>
			{/each}

			<svelte:fragment slot="expandableContent">
				{#if eventLogHasRecordingExpand(row.event)}
					<CameraEventExpanded event={row.event} {isMobile} />
				{:else}
					<div class="d-flex align-items-center justify-content-between">
						{#if $showNewLayout}
							<LabelListV2
								a11yProps={{
									isInteractive: false,
								}}
								labelList={row.labelListData}
								size={isMobile ? 's' : 'm'}
								backgroundColor="--color-1"
							/>
						{:else}
							<LabelList labelList={row.labelListData} size={isMobile ? 's' : 'm'} />
						{/if}

						{#if isMobile}
							<IconButton
								class="ml-s flex-shrink-0"
								shape="rounded"
								variant="muted"
								icon="fas fa-up-right-and-down-left-from-center"
								aria-label={$format('short_info', { default: 'Info' })}
								on:click={() => openDetails(row)}
							/>
						{/if}
					</div>
				{/if}
			</svelte:fragment>
		</ExpandableTableRow>
	{/each}
	<!-- Table Body -->
</ExpandableTable>

<EventDetailsModalV2
	showModal={Boolean(detailsRow)}
	event={detailsRow?.event}
	labelList={detailsRow?.labelListData ?? []}
	{isMobile}
	on:close={closeDetails}
/>
